/**
 * Maps Spotlight / shortcut create action ids to CreateDialogHost opens.
 */

import type { AppointmentCreatePrefill } from '@/modules/appointment/utils/appointmentViewParams';
import type { AddLeadPrefill } from '@/modules/lead/utils/leadForm';
import type { AddPatientPrefill } from '@/modules/patient/utils/patientForm';
import type { TaskCreatePrefill } from '@/modules/task/utils/taskViewParams';
import { parseTaskCreatePrefill } from '@/modules/task/utils/taskViewParams';

export type CreateHostKind =
  | 'appointment'
  | 'patient'
  | 'lead'
  | 'task'
  | 'project'
  | 'expense'
  | 'advance-request'
  | 'treatment-catalog';

export type CreateHostOpen =
  | { kind: 'appointment'; prefill: AppointmentCreatePrefill }
  | { kind: 'patient'; prefill?: AddPatientPrefill }
  | { kind: 'lead'; prefill?: AddLeadPrefill }
  | { kind: 'task'; prefill: TaskCreatePrefill }
  | { kind: 'project' }
  | { kind: 'expense' }
  | { kind: 'advance-request' }
  | { kind: 'treatment-catalog' };

const APPOINTMENT_IDS = new Set([
  'create-appointment',
  'ctx-create-appointment',
  'ctx-create-appointment-today',
]);

const PATIENT_IDS = new Set(['create-patient']);

const LEAD_IDS = new Set(['create-lead']);

const TASK_IDS = new Set(['create-task', 'ctx-create-task']);

const PROJECT_IDS = new Set(['create-project']);

const EXPENSE_IDS = new Set(['create-expense']);

const ADVANCE_REQUEST_IDS = new Set(['create-advance-request']);

const TREATMENT_CATALOG_IDS = new Set(['create-treatment-catalog']);

const actionIdBase = (id: string): string => {
  const colon = id.indexOf(':');
  return colon === -1 ? id : id.slice(0, colon);
};

const appointmentPrefillFromState = (state?: object): AppointmentCreatePrefill => {
  if (!state || typeof state !== 'object') return {};
  const raw = (state as { appointmentCreate?: AppointmentCreatePrefill }).appointmentCreate;
  return raw && typeof raw === 'object' ? raw : {};
};

const patientPrefillFromState = (state?: object): AddPatientPrefill | undefined => {
  if (!state || typeof state !== 'object') return undefined;
  const raw = (state as { prefill?: AddPatientPrefill }).prefill;
  return raw && typeof raw === 'object' ? raw : undefined;
};

const leadPrefillFromState = (state?: object): AddLeadPrefill | undefined => {
  if (!state || typeof state !== 'object') return undefined;
  const raw = (state as { prefill?: AddLeadPrefill }).prefill;
  return raw && typeof raw === 'object' ? raw : undefined;
};

const taskPrefillFrom = (state?: object, path?: string): TaskCreatePrefill => {
  if (state && typeof state === 'object') {
    const raw = (state as { taskCreate?: TaskCreatePrefill }).taskCreate;
    if (raw && typeof raw === 'object') return raw;
  }
  if (path) {
    try {
      const url = new URL(path, 'http://local');
      return parseTaskCreatePrefill(url.searchParams);
    } catch {
      return {};
    }
  }
  return {};
};

/**
 * Resolve a Spotlight action / recent / shortcut id to an in-place create open.
 * Returns null for navigate-only go/command actions.
 */
export const resolveCreateHostAction = (
  actionId: string,
  opts?: { state?: object; path?: string },
): CreateHostOpen | null => {
  const base = actionIdBase(actionId);

  if (APPOINTMENT_IDS.has(base)) {
    return {
      kind: 'appointment',
      prefill: appointmentPrefillFromState(opts?.state),
    };
  }
  if (PATIENT_IDS.has(base)) {
    return { kind: 'patient', prefill: patientPrefillFromState(opts?.state) };
  }
  if (LEAD_IDS.has(base)) {
    return { kind: 'lead', prefill: leadPrefillFromState(opts?.state) };
  }
  if (TASK_IDS.has(base)) {
    return { kind: 'task', prefill: taskPrefillFrom(opts?.state, opts?.path) };
  }
  if (PROJECT_IDS.has(base)) {
    return { kind: 'project' };
  }
  if (EXPENSE_IDS.has(base)) {
    return { kind: 'expense' };
  }
  if (ADVANCE_REQUEST_IDS.has(base)) {
    return { kind: 'advance-request' };
  }
  if (TREATMENT_CATALOG_IDS.has(base)) {
    return { kind: 'treatment-catalog' };
  }
  return null;
};
